import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import { Link, useParams } from "react-router-dom";
import { setArticleDetails } from "../../../redux/actions/product_actions";
import { fetchArticle, fetchArticles } from "../../../api/articles";

// Premium SVG Icons
const CalendarIcon = () => (
    <svg className="w-4 h-4 text-slate-400 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);

const HeartIcon = () => (
    <svg className="w-4 h-4 text-rose-500 mr-1.5 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
        <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
    </svg>
);

const ArrowLeftIcon = () => (
    <svg className="w-4 h-4 mr-1.5 text-blue-600 group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
);

const ArticleDetailPage = () => {
    const dispatch = useDispatch();
    const [article, setArticle] = useState(null);
    const [articles, setArticles] = useState([]);
    const [update, setUpdate] = useState('');
    const params = useParams();

    const getArticle = async () => {
        try {
            const articleDetails = await fetchArticle(params.id);
            if (articleDetails) {
                dispatch(setArticleDetails(articleDetails));
                setArticle(articleDetails);
                moment.locale('ru');
                setUpdate(moment(articleDetails.created_at, 'YYYYMMDD, h:mm:ss a').calendar());
            }
        } catch (error) {
            console.error("Failed to fetch article:", error);
        }
    };

    const getArticles = async () => {
        try {
            const fetchedArticles = await fetchArticles({ 'limit': 10, 'offset': 0 });
            if (fetchedArticles) {
                setArticles(fetchedArticles.filter(item => item.id !== Number(params.id)));
            }
        } catch (error) {
            console.error("Failed to fetch articles:", error);
        }
    };

    useEffect(() => {
        getArticle();
        getArticles();
        window.scrollTo(0, 0);
    }, [params.id]);

    if (!article) {
        return (
            <div className="flex justify-center items-center min-h-[500px]">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-blue-600 border-r-2 border-slate-100"></div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
            {/* Breadcrumb / Back Link */}
            <div>
                <Link
                    to="/articles"
                    className="group inline-flex items-center text-sm font-semibold text-blue-600 hover:text-blue-700 no-underline transition"
                >
                    <ArrowLeftIcon />
                    Назад к статьям
                </Link>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Main Article Content */}
                <div className="lg:col-span-8 space-y-6">
                    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                        {/* Featured Image */}
                        <div className="w-full h-[320px] md:h-[420px] overflow-hidden bg-slate-50 relative">
                            <img 
                                src={article.image} 
                                alt={article.title} 
                                className="w-full h-full object-cover"
                            />
                        </div>
                        
                        {/* Article Text Details */}
                        <div className="p-6 md:p-8 space-y-4">
                            {/* Meta Metrics */}
                            <div className="flex flex-wrap items-center gap-4 text-xs font-semibold text-slate-500 border-b border-slate-50 pb-4">
                                <span className="flex items-center bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-100">
                                    <CalendarIcon />
                                    Опубликовано: {update}
                                </span>
                                <span className="flex items-center bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-100">
                                    <HeartIcon />
                                    Лайков: {article.likes_count}
                                </span>
                            </div>
                            
                            {/* Title */}
                            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-800 tracking-tight leading-tight">
                                {article.title}
                            </h1>
                            
                            {/* Rich Text Body */}
                            <div 
                                className="prose prose-slate max-w-full text-slate-600 text-[15px] leading-relaxed space-y-4 pt-2 font-medium"
                                dangerouslySetInnerHTML={{ __html: article.text }}
                            />
                        </div>
                    </div>
                </div>

                {/* Related Articles Sidebar */}
                <div className="lg:col-span-4 space-y-5">
                    <h2 className="text-lg font-bold text-slate-800 tracking-tight pb-2 border-b border-slate-100">
                        Другие новости
                    </h2>
                    
                    <div className="space-y-4">
                        {articles.slice(0, 5).map((relatedArticle) => (
                            <Link 
                                key={relatedArticle.id} 
                                to={`/article/${relatedArticle.id}`} 
                                className="group block no-underline p-3 rounded-xl border border-slate-50 hover:border-blue-100 hover:bg-slate-50/50 transition-all duration-300 bg-white"
                            >
                                <div className="flex gap-4">
                                    <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-slate-50">
                                        <img 
                                            src={relatedArticle.image} 
                                            alt={relatedArticle.title} 
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                    </div>
                                    <div className="flex-1 flex flex-col justify-between min-w-0">
                                        <h3 className="font-semibold text-slate-800 text-[13px] line-clamp-2 group-hover:text-blue-600 transition-colors">
                                            {relatedArticle.title}
                                        </h3>
                                        <div 
                                            className="text-slate-400 text-[11px] line-clamp-1 mt-1 font-medium"
                                            dangerouslySetInnerHTML={{ __html: relatedArticle.text }}
                                        />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ArticleDetailPage;